import { useEffect } from 'react';
import { create } from 'zustand';

import { useT } from '../i18n';
import { appendAppLog } from '../platform/appLog';
import { Icon } from './Icon';
import { resolveToastText, type PersistenceKey } from './toastText';

/**
 * Kurze Einblendung am unteren Rand: Hinweis beim Wiedereinstieg, Warnung bei einem
 * fehlgeschlagenen Schreib- oder Löschvorgang, Nachricht des Prüfungsleiters. Kein Dialog —
 * sie unterbricht die Bewertung nicht.
 *
 * **Zwei Arten, und der Unterschied ist wichtig.**
 *
 * `show()` nimmt einen **Sprachschlüssel**, keinen fertigen Text: der Text hängt an der Sprache
 * des Geräts (`t.persistence[…]`), und ein Prüfer, der auf Englisch oder Französisch gestellt
 * hat, soll nie einen deutschen Rohtext oder — schlimmer — einen Programmierschlüssel wie
 * „writeFailed" auf dem Bogen sehen. Der Katalog wird erst beim Rendern nachgeschlagen (nicht
 * schon in `show()`), damit ein Sprachwechsel, während die Einblendung steht, den richtigen
 * Text zeigt.
 *
 * `zeigeText()` ist die **einzige** Ausnahme: was der Prüfungsleiter tippt (`message` in der
 * Antwort auf `POST /rest/log`, s. `server/classic/senden.ts`), steht in keinem Katalog. Der
 * Text wird gezeigt, wie er kommt — React setzt ihn als Text ein, nicht als Markup.
 */

/**
 * Wie lange eine Einblendung steht.
 *
 * ⚠️ **Die Nachricht des Prüfungsleiters bleibt länger.** Der Server liefert sie **genau
 * einmal** aus und löscht sie dann; wer sie verpasst, bekommt sie nie wieder. Vier Sekunden
 * sind zu kurz für einen Prüfer, der gerade auf den Prüfling sieht und nicht auf das iPad.
 */
export const STANDZEIT_MS = { schluessel: 4000, text: 12000, aufnahme: 2000 } as const;

export type ToastInhalt =
  | { art: 'schluessel'; key: PersistenceKey }
  | { art: 'text'; text: string }
  /** Einstellungsnamen, die Stage nicht kennt (`platform/unbekannteEinstellungen.ts`). */
  | { art: 'unbekannt'; namen: string[] }
  /** Zeit, die Relays Aufsicht der laufenden Station gegeben hat (`TIME_ADD`). */
  | { art: 'zugabe'; sekunden: number };

interface ToastState {
  inhalt: ToastInhalt | null;
  show: (key: PersistenceKey) => void;
  /** Freier Text vom Server. Leer heisst: nichts einblenden. */
  zeigeText: (text: string) => void;
  /** Unbekannte Einstellungen aus einem `SET_SETTINGS`. Leer heisst: nichts einblenden. */
  zeigeUnbekannte: (namen: string[]) => void;
  /**
   * Eine Zugabe der Aufsicht — nur ein Hinweis, ohne Bestätigung (Entscheidung des Nutzers,
   * 2026-10-08): die Uhr zeigt die Zeit ohnehin, der Prüfer soll nur wissen, warum.
   */
  zeigeZugabe: (sekunden: number) => void;
  /**
   * Relays `SCREENSHOT` hat ein Bild genommen: kurz ein Kamerasymbol (§2.2). Eigenes Feld, nicht
   * `inhalt` — es soll keine stehende Einblendung verdrängen.
   */
  aufnahme: boolean;
  zeigeAufnahme: () => void;
  hide: () => void;
}

/**
 * Welche Schlüssel einen Fehlschlag melden — sie gehen als `error` ins Log, alles Übrige als
 * `info`.
 *
 * ⚠️ **Ausdrücklich aufgezählt, nicht am Namen erkannt.** Ein neuer Schlüssel gilt bis zum
 * Eintrag hier als Hinweis; er steht dann immer noch im Log, nur nicht unter den Fehlern.
 * Andersherum wäre schlimmer: „Stand wiederhergestellt" als Fehler zu führen entwertet das
 * Filtern nach Fehlern, und genau dafür ist die Stufe da.
 */
const FEHLSCHLAEGE: ReadonlySet<PersistenceKey> = new Set<PersistenceKey>([
  'writeFailed',
  'deleteFailed',
  'startupFailed',
]);

/**
 * ⭐ **Jede Einblendung schreibt ihre Zeile — hier, nicht an der Aufrufstelle** (2026-09-25).
 *
 * Das ist die Lehre aus dem Befund vom 2026-09-24: vier Stellen zeigten dem Prüfer „Konnte
 * nicht gesichert werden" und schrieben nichts dazu; am Gerät war hinterher nicht
 * herauszufinden, welche davon gefeuert hatte. Jede dieser Stellen einzeln nachzurüsten hilft
 * nur, bis jemand eine fünfte baut. Im Speicher selbst kann es niemand mehr vergessen.
 *
 * ⚠️ **Protokolliert wird der Schlüssel, nicht der Text.** Der Katalog wird erst beim Rendern
 * nachgeschlagen (s. Kopf dieser Datei); der Schlüssel ist über alle drei Sprachen derselbe
 * und im Log damit auffindbar, ein übersetzter Text wäre es nicht.
 *
 * ⚠️ **Wirft nie.** Die Einblendung ist das Wichtigere von beiden: der Prüfer muss sie sehen,
 * auch wenn das Protokoll gerade nicht mitschreiben kann.
 */
function protokolliere(text: string, stufe: 'info' | 'error'): void {
  try {
    void appendAppLog(text, stufe).catch(() => undefined);
  } catch {
    // Ein Protokoll, das selbst zum Fehler wird, hilft niemandem — und dürfte hier nicht
    // dazu führen, dass die Meldung ausbleibt.
  }
}

export const useToastStore = create<ToastState>()((set) => ({
  inhalt: null,
  show: (key) => {
    protokolliere('Einblendung: ' + key, FEHLSCHLAEGE.has(key) ? 'error' : 'info');
    set({ inhalt: { art: 'schluessel', key } });
  },
  zeigeText: (text) => {
    // Eine leere Nachricht ist keine Nachricht: eine leere Fläche über dem Bogen sähe aus wie
    // ein Fehler der App, und der Prüfer suchte nach etwas, das nie da war.
    const sauber = text.trim();
    if (sauber === '') return;
    // ⚠️ **Auch sie ins Log.** Der Server liefert `message` genau einmal aus und löscht sie
    // dann (s. `server/classic/senden.ts`); ohne diese Zeile gäbe es hinterher keinen Beleg,
    // dass sie je auf dem Gerät stand.
    protokolliere('Einblendung (Nachricht des Prüfungsleiters): ' + sauber, 'info');
    set({ inhalt: { art: 'text', text: sauber } });
  },
  zeigeUnbekannte: (namen) => {
    if (namen.length === 0) return;
    protokolliere('Einblendung: unbekannte Einstellungen', 'info');
    set({ inhalt: { art: 'unbekannt', namen } });
  },
  zeigeZugabe: (sekunden) => {
    protokolliere('Einblendung: Zugabe ' + String(sekunden) + ' s', 'info');
    set({ inhalt: { art: 'zugabe', sekunden } });
  },
  aufnahme: false,
  zeigeAufnahme: () => {
    protokolliere('Einblendung: Bildschirmfoto für Relay', 'info');
    set({ aufnahme: true });
  },
  hide: () => set({ inhalt: null }),
}));

export function Toast() {
  const inhalt = useToastStore((s) => s.inhalt);
  const hide = useToastStore((s) => s.hide);
  const t = useT();
  const aufnahme = useToastStore((s) => s.aufnahme);

  useEffect(() => {
    if (!aufnahme) return;
    const uhr = setTimeout(() => useToastStore.setState({ aufnahme: false }), STANDZEIT_MS.aufnahme);
    return () => clearTimeout(uhr);
  }, [aufnahme]);
  const symbol = aufnahme && (
    <div role="status" className="aufnahme-symbol" aria-label={t.aufnahmeSymbol}>
      <Icon name="camera" size={18} />
    </div>
  );

  // Trifft während der Standzeit eine neue Einblendung ein, räumt die Aufräumfunktion den
  // alten Timer ab und dieser Effekt setzt einen neuen — die zweite Meldung verdrängt die
  // erste, statt sich dahinter einzureihen oder gemeinsam mit ihr zu verschwinden.
  useEffect(() => {
    if (!inhalt) return;
    // Namen wollen gelesen werden — dieselbe lange Standzeit wie eine Nachricht. Die Zugabe
    // ist ein kurzer Hinweis wie ein Schlüssel.
    const kurz = inhalt.art === 'schluessel' || inhalt.art === 'zugabe';
    const uhr = setTimeout(hide, STANDZEIT_MS[kurz ? 'schluessel' : 'text']);
    return () => clearTimeout(uhr);
  }, [inhalt, hide]);

  if (!inhalt) return symbol || null;
  return (
    <>
      {symbol}
      <div role="status" aria-live="polite" className="toast">
        {inhalt.art === 'text'
          ? inhalt.text
          : inhalt.art === 'unbekannt'
            ? t.unbekannteEinstellungen(inhalt.namen.join(', '))
            : inhalt.art === 'zugabe'
              ? t.aufsicht.zugabe(Math.floor(inhalt.sekunden / 60), inhalt.sekunden % 60)
              : resolveToastText(t, inhalt.key)}
      </div>
    </>
  );
}
